import { useEffect } from 'react'

import { GameId } from '@common'
import { StoreBonusStatusResponse, StoreMission } from '@common/models/bonus'
import cx from 'classnames'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'

import BsUnavailableImg from '@/assets/images/storefronts/brawlstars/bonus-track-unavailable.png'
import CocUnavailableImg from '@/assets/images/storefronts/clashofclans/bonus-track-unavailable.png'
import CrUnavailableImg from '@/assets/images/storefronts/clashroyale/bonus-track-unavailable.png'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { getTimeRemaining } from '@/utils/timer'
import { useAppSelector } from '@/utils/useAppSelector'

import { BonusMissionItem } from './BonusMissionItem'
import { CRBonusMissionItem, type CRStoreMissionType } from './CRBonusMissionItem'
import { useTransformMissionsData } from '../../../bonusChallenge/isCurrentSeason'
import { BonusTimeRemaining } from '../../shared/BonusTimeRemaining'

import styles from './bonusMissions.module.scss'

interface BonusPointsProps {
  bonusStatus: StoreBonusStatusResponse
}

const MissionItems = ({
  missionsData,
  isRoyale,
}: {
  missionsData: StoreMission[] | CRStoreMissionType[]
  isRoyale: boolean
}) => {
  return (
    <>
      {missionsData.map((mission, index) => {
        return isRoyale ? (
          <CRBonusMissionItem key={index} mission={mission as CRStoreMissionType} />
        ) : (
          <BonusMissionItem key={index} mission={mission as StoreMission} />
        )
      })}
    </>
  )
}

const FALLBACK_TIMEZONE = 'UTC'

function getUnavailableImg(gameSlug: GameId) {
  switch (gameSlug) {
    case 'brawlstars':
      return BsUnavailableImg
    case 'clashroyale':
      return CrUnavailableImg
    default:
      return CocUnavailableImg
  }
}

export const BonusMissions = ({ bonusStatus }: BonusPointsProps) => {
  const t = useTranslations()
  const gameSlug = useGameSlug()
  const locale = useLocale()
  const isRoyale = gameSlug === 'clashroyale'
  const profileTimezone = useAppSelector(
    (state) => state.user.profile?.applications?.find((app) => app.application === 'clashroyale')?.timezone,
  )

  useEffect(() => {
    const element = document.getElementById('bonusTrackWrapper')
    element?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [])

  const missions = bonusStatus?.missions?.missions

  const CRtimezone = profileTimezone || FALLBACK_TIMEZONE
  const CRmissionsData = useTransformMissionsData(bonusStatus, CRtimezone, new Date(), locale)

  const missionsTimeLeft = bonusStatus?.missions?.endTimeMillis || 0
  const remainingTime = getTimeRemaining(missionsTimeLeft)
  const CRremainingTime = getTimeRemaining(CRmissionsData.currentCadenceEnd)
  const showRemainingTime = isRoyale ? CRremainingTime : bonusStatus?.endTimeMillis && bonusStatus.endTimeMillis > 0

  const missionsData = isRoyale ? CRmissionsData.missions : missions

  const missionsAvailable = !!(missionsData && missionsData.length > 0)

  const bonusMissionsUnavailableTextArray = t('missions_unavailable').split(' ')

  const unavailableImg = getUnavailableImg(gameSlug)

  return (
    <div className={cx(styles.contentContainer, { [styles.unavailable]: !missionsAvailable })}>
      {missionsAvailable ? (
        <>
          <StoreTypography
            tag="h3"
            textSize="lg"
            overrideFont="supercellHeadline"
            style="plain"
            className={styles.missionsTitle}
          >
            {t('earn_points_completing_missions')}
          </StoreTypography>

          {!!showRemainingTime && (
            <BonusTimeRemaining remainingTime={isRoyale ? CRremainingTime : remainingTime} gameSlug={gameSlug} />
          )}

          <div className={styles.missionsContainer}>
            <MissionItems missionsData={missionsData} isRoyale={isRoyale} />
          </div>

          <div className={styles.infoContainer}>
            <span className={styles.infoCircle}>!</span>
            <StoreTypography tag="h6" textSize="xs" style="plain" overrideFont="supercellText">
              {t('bonus_time_appear')}
            </StoreTypography>
          </div>
        </>
      ) : (
        <>
          <div className={styles.missionsTitle}>
            <StoreTypography tag="h3" textSize="lg" overrideFont="supercellHeadline" style="plain">
              {bonusMissionsUnavailableTextArray[0]}
            </StoreTypography>
            <StoreTypography tag="h3" textSize="lg" overrideFont="supercellHeadline" style="plain">
              {bonusMissionsUnavailableTextArray[1]}
            </StoreTypography>
          </div>

          <div className={styles.unavailableImg}>
            <Image src={unavailableImg} alt="bonus-unavailable" fill />
          </div>
          <StoreTypography tag="h3" textSize="sm" style="plain" overrideFont="supercellText">
            {t('bonus_track_check_back')}
          </StoreTypography>
        </>
      )}
    </div>
  )
}
